import { getSlotKey, parseSlotKey, type BlockMap, type SlotsState } from './slots';

export const getSlotKeyFromPlace = (
  blockMaps: BlockMap[],
  block: string,
  row: string,
  slot: string,
): string | undefined => {
  const blockMap = blockMaps.find(
    (item) => item.id.replace(/-\d+$/, '').toUpperCase() === block.toUpperCase(),
  );

  if (!blockMap) return undefined;

  return getSlotKey(blockMap.startCol + Number(slot) - 1, blockMap.startRow + Number(row) - 1);
};

export const slotKeyToPlaceKey = (slotKey: string, blockMaps: BlockMap[]): string | undefined => {
  const { col, row } = parseSlotKey(slotKey);

  for (const blockMap of blockMaps) {
    const { startCol, startRow, colsCount, rowsCount, id } = blockMap;

    if (
      col >= startCol &&
      col < startCol + colsCount &&
      row >= startRow &&
      row < startRow + rowsCount
    ) {
      const blockName = id.replace(/-\d+$/, '');

      return `${blockName}-${Math.round(row - startRow + 1)}-${Math.round(col - startCol + 1)}`;
    }
  }

  return undefined;
};

/** For 40ft containers the second occupied place is always the next row with the same slot. */
export const resolve40ftPlaceSlotKeys = (
  blockMaps: BlockMap[],
  slotNames: Record<string, string>,
  block: string,
  row: string,
  slot: string,
): string[] => {
  const firstSlotKey = getSlotKeyFromPlace(blockMaps, block, row, slot);

  if (!firstSlotKey || !slotNames[firstSlotKey]) return [];

  const secondSlotKey = getSlotKeyFromPlace(blockMaps, block, String(Number(row) + 1), slot);

  if (!secondSlotKey || !slotNames[secondSlotKey]) return [];

  return [firstSlotKey, secondSlotKey];
};

export const resolvePlaceSlotKeys = (
  slotsState: SlotsState,
  block: string,
  row: string,
  slot: string,
  containerLength = 20,
): string[] => {
  const blockMaps = slotsState.blockMaps ?? [];
  const slotNames = slotsState.slotNames ?? {};
  const targetSlotKey = getSlotKeyFromPlace(blockMaps, block, row, slot);

  if (!targetSlotKey || !slotNames[targetSlotKey]) return [];

  if (containerLength !== 40) return [targetSlotKey];

  return resolve40ftPlaceSlotKeys(blockMaps, slotNames, block, row, slot);
};

export const buildContainerSceneKey = (
  slotsState: SlotsState,
  block: string,
  row: string,
  slot: string,
  containerLength = 20,
): string => {
  const blockMaps = slotsState.blockMaps ?? [];
  const slotKeys = resolvePlaceSlotKeys(slotsState, block, row, slot, containerLength);
  const placeKeys = slotKeys
    .map((slotKey) => slotKeyToPlaceKey(slotKey, blockMaps))
    .filter(Boolean);

  if (placeKeys.length) return placeKeys.join(',');

  if (containerLength === 40) {
    return `${block}-${row}-${slot},${block}-${Number(row) + 1}-${slot}`;
  }

  return `${block}-${row}-${slot}`;
};

export const enrich40ftSlotKeys = (
  slotKeys: string[],
  slotNames: Record<string, string>,
  length?: number,
): string[] => {
  if (length !== 40 || slotKeys.length !== 1) return slotKeys;

  const { col, row } = parseSlotKey(slotKeys[0]);
  const secondSlotKey = getSlotKey(col, row + 1);

  if (!slotNames[secondSlotKey] || slotKeys.includes(secondSlotKey)) return slotKeys;

  return [slotKeys[0], secondSlotKey];
};

export type YardBlockOption = {
  block: string;
  rows: string[];
  slots: string[];
};
